import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import React, {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  type ListRange,
  type ScrollSeekConfiguration,
  type ScrollSeekPlaceholderProps,
  Virtuoso,
  type VirtuosoHandle,
} from 'react-virtuoso';
import {twMerge} from 'tailwind-merge';

import {JUMP_TO_BOTTOM_THRESHOLD_PX} from './constants';
import {useAutoScroll} from './hooks/useAutoScroll';
import {useLogListScroller} from './hooks/useLogListScroller';
import {useVirtuosoComponents} from './hooks/useVirtuosoComponents';
import {LogJumpButton} from './LogJumpButton';
import {type LogLabelFilterOption} from './LogLabelFilter';
import {type LogLineData, LogLineRenderer} from './LogLineRenderer';
import {type LogTimestampFormatters} from './logTimestamp';

const GLOBAL_LOG_SCROLL_SEEK_ENTER_VELOCITY = 2600;
const GLOBAL_LOG_SCROLL_SEEK_EXIT_VELOCITY = 120;
const GLOBAL_LOG_LOADING_INDICATOR_HIDE_DELAY_MS = 250;
const GLOBAL_LOG_MAX_HEIGHT_ESTIMATE_COUNT = 250_000;
const GLOBAL_LOG_SCROLL_STATUS_ROUNDING = 100;
const GLOBAL_LOG_EXTRA_RENDERED_ROW_COUNT = 100;

export interface GlobalLogViewerPagination {
  initialTopMostItemIndex?: number;
  isLoadingRange?: boolean;
  isRetryingRange?: boolean;
  lineByNumber: Map<number, LogLineData>;
  newLineCount?: number;
  onAtCurrentChange?: (isAtCurrent: boolean) => void;
  onJumpToCurrent?: () => void;
  onJumpToTop?: () => void;
  onRangeChanged: (range: ListRange) => void;
  onRetryRange?: () => void;
  rangeFetchError?: Error | string;
  totalCount: number;
}

export interface GlobalLogListProps {
  filteredLabels: readonly LogLabelFilterOption[];
  filteredLines: readonly LogLineData[];
  formatters: LogTimestampFormatters;
  globalPagination: GlobalLogViewerPagination;
  isPolling?: boolean;
  isTimestampVisible: boolean;
  lineHeight: number;
  maxLabelLength: number;
  onJumpToBottom?: () => void;
  // Visible range is owned by the shell so it survives the dense (search/filter)
  // interlude and can be restored when the global list re-activates.
  onVisibleRangeChange: (range: ListRange) => void;
  scrollTo: (row: number, align?: 'start' | 'center' | 'end') => void;
  virtuosoRef: React.RefObject<VirtuosoHandle | null>;
  visibleRange: ListRange | null;
}

/**
 * Sparse, line-number-indexed log list. The Virtuoso list is sized to the full
 * log (`totalCount`); only loaded windows live in `lineByNumber`, and missing
 * rows render placeholders until their page arrives. Owns range fetching,
 * scroll-seek, jump-to-top/current, and the scroll-position status.
 */
export const GlobalLogList: React.FC<GlobalLogListProps> = ({
  filteredLabels,
  filteredLines,
  formatters,
  globalPagination,
  isPolling,
  isTimestampVisible,
  lineHeight,
  maxLabelLength,
  onJumpToBottom,
  onVisibleRangeChange,
  scrollTo,
  virtuosoRef,
  visibleRange,
}) => {
  const {
    totalCount: globalLineCount,
    lineByNumber,
    initialTopMostItemIndex,
    onAtCurrentChange,
    onJumpToCurrent,
    onJumpToTop,
    onRangeChanged,
    newLineCount,
    isLoadingRange,
    isRetryingRange,
    onRetryRange,
    rangeFetchError,
  } = globalPagination;

  const [isGlobalScrollSeeking, setIsGlobalScrollSeeking] = useState(false);

  const firstVisibleIndex =
    visibleRange?.startIndex ?? initialTopMostItemIndex ?? 0;
  const lastVisibleIndex = visibleRange?.endIndex ?? firstVisibleIndex;

  const hasMissingVisibleGlobalLines = useMemo(() => {
    if (visibleRange == null) {
      return false;
    }

    for (
      let index = visibleRange.startIndex;
      index <= visibleRange.endIndex;
      index++
    ) {
      if (!lineByNumber.has(index)) {
        return true;
      }
    }
    return false;
  }, [lineByNumber, visibleRange]);

  const scrollToBottom = useCallback(() => {
    const bottomIndex = globalLineCount - 1;
    if (bottomIndex >= 0) {
      scrollTo(bottomIndex, 'end');
    }
  }, [globalLineCount, scrollTo]);

  const {
    followOutput,
    handleAtBottomStateChange,
    handleIsScrolling: handleIsScrollingForAutoScroll,
    handleJumpToBottom,
    handleRangeChanged,
    handleScrollerRef,
  } = useAutoScroll({
    firstItemIndex: 0,
    startsUnpinned: false,
    lineCount: globalLineCount,
    onJumpToBottom,
    scrollToBottom,
  });

  const handleRangeChangedForViewer = useCallback(
    (range: ListRange) => {
      handleRangeChanged();
      onVisibleRangeChange(range);
      onRangeChanged(range);
      const nearCurrent =
        globalLineCount - 1 - range.endIndex <=
        Math.ceil(JUMP_TO_BOTTOM_THRESHOLD_PX / lineHeight);
      onAtCurrentChange?.(nearCurrent);
    },
    [
      onRangeChanged,
      globalLineCount,
      lineHeight,
      onAtCurrentChange,
      onVisibleRangeChange,
      handleRangeChanged,
    ]
  );

  const handleIsScrolling = useCallback(
    (scrolling: boolean) => {
      if (!scrolling) {
        setIsGlobalScrollSeeking(false);
      }
      handleIsScrollingForAutoScroll(scrolling);
    },
    [handleIsScrollingForAutoScroll]
  );

  const maxLineNumLength = globalLineCount.toString().length;
  const {measurer, heightEstimates, setScrollerRef} = useLogListScroller({
    handleScrollerRef,
    lineHeight,
    lines: filteredLines,
    maxLabelLength,
    maxLineNumLength,
    showLabel: filteredLabels.length > 0,
    showLineNumber: maxLineNumLength > 0,
    showTimestamp: isTimestampVisible,
  });
  const globalLogHeightByNumber = useMemo(
    () => buildGlobalLogHeightByNumber(filteredLines, heightEstimates),
    [filteredLines, heightEstimates]
  );
  const virtuosoHeightEstimates = useMemo(
    () =>
      buildGlobalLogHeightEstimates({
        defaultHeight: lineHeight + 4,
        heightByNumber: globalLogHeightByNumber,
        totalCount: globalLineCount,
      }),
    [globalLineCount, globalLogHeightByNumber, lineHeight]
  );

  const itemContent = useCallback(
    (index: number) => {
      const line = lineByNumber.get(index);
      if (!line) {
        return <GlobalLogPlaceholderLine height={lineHeight + 4} />;
      }

      return (
        <LogLineRenderer
          line={line}
          index={index}
          showTimestamp={isTimestampVisible}
          formatters={formatters}
          showLabel={filteredLabels.length > 0}
          showLineNumber={line.lineNum != null}
          maxLineNumLength={maxLineNumLength}
          maxLabelLength={filteredLabels.length > 0 ? maxLabelLength : 0}
          className="px-16 py-2"
          style={getGlobalLogLineHeightStyle(
            globalLogHeightByNumber?.get(index)
          )}
        />
      );
    },
    [
      filteredLabels,
      formatters,
      globalLogHeightByNumber,
      lineByNumber,
      isTimestampVisible,
      lineHeight,
      maxLabelLength,
      maxLineNumLength,
    ]
  );

  const computeItemKey = useCallback(
    (index: number) =>
      lineByNumber.get(index)?.id ?? `log-placeholder-${index}`,
    [lineByNumber]
  );

  const {components: virtuosoComponents, context: virtuosoContext} =
    useVirtuosoComponents({
      isPolling: isPolling ?? false,
      isLoadingPrevious: !!isLoadingRange && hasMissingVisibleGlobalLines,
      isRetryingPrevious: isRetryingRange ?? false,
      loadPreviousError: rangeFetchError,
      onRetryPrevious: onRetryRange,
      isLoadingNext: false,
      isRetryingNext: false,
      loadNextError: undefined,
      onRetryNext: undefined,
    });
  const components = useMemo(
    () => ({
      ...virtuosoComponents,
      ScrollSeekPlaceholder: ({height, index}: ScrollSeekPlaceholderProps) => (
        <GlobalLogScrollSeekPlaceholder
          height={height}
          estimatedHeight={globalLogHeightByNumber?.get(index)}
        />
      ),
    }),
    [globalLogHeightByNumber, virtuosoComponents]
  );
  const scrollSeekConfiguration = useMemo<ScrollSeekConfiguration>(
    () => ({
      enter: velocity => {
        const shouldEnter =
          Math.abs(velocity) > GLOBAL_LOG_SCROLL_SEEK_ENTER_VELOCITY;
        if (shouldEnter) {
          setIsGlobalScrollSeeking(true);
        }
        return shouldEnter;
      },
      exit: velocity => {
        const shouldExit =
          Math.abs(velocity) < GLOBAL_LOG_SCROLL_SEEK_EXIT_VELOCITY;
        if (shouldExit) {
          setIsGlobalScrollSeeking(false);
        }
        return shouldExit;
      },
    }),
    []
  );

  const handleJumpToTop = useCallback(() => {
    onJumpToTop?.();
    scrollTo(0, 'start');
  }, [onJumpToTop, scrollTo]);
  const handleJumpToCurrent = useCallback(() => {
    onJumpToCurrent?.();
    handleJumpToBottom();
  }, [onJumpToCurrent, handleJumpToBottom]);
  const handleLogJumpShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        isEditableKeyboardTarget(event.target) ||
        event.altKey
      ) {
        return;
      }

      if (isJumpToLogTopShortcut(event)) {
        event.preventDefault();
        handleJumpToTop();
      } else if (isJumpToLogCurrentShortcut(event)) {
        event.preventDefault();
        handleJumpToCurrent();
      }
    },
    [handleJumpToCurrent, handleJumpToTop]
  );
  useEffect(() => {
    // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
    window.addEventListener('keydown', handleLogJumpShortcut);
    return () => {
      window.removeEventListener('keydown', handleLogJumpShortcut);
    };
  }, [handleLogJumpShortcut]);

  const showGlobalLoadingIndicator =
    !!isLoadingRange && hasMissingVisibleGlobalLines;
  const showGlobalScrollStatus =
    visibleRange != null &&
    (isGlobalScrollSeeking || showGlobalLoadingIndicator);
  const {
    isMounted: isGlobalScrollStatusMounted,
    isVisible: isGlobalScrollStatusVisible,
  } = useDelayedPresence(
    showGlobalScrollStatus,
    GLOBAL_LOG_LOADING_INDICATOR_HIDE_DELAY_MS
  );

  const showJumpToTop = firstVisibleIndex > 0;
  const isGlobalFarFromCurrent =
    globalLineCount - 1 - lastVisibleIndex >
    Math.ceil(JUMP_TO_BOTTOM_THRESHOLD_PX / lineHeight);
  const showJumpToCurrent = isGlobalFarFromCurrent || !!newLineCount;
  const jumpToCurrentLabel = newLineCount
    ? `${newLineCount} new ${newLineCount === 1 ? 'line' : 'lines'}`
    : 'Current';

  return (
    <>
      {
        // Render a hidden element used to determine the count of characters
        // that fit on a single line and the width of the scrollbar.
        measurer
      }
      <Virtuoso
        // Use 'atBottomStateChange' instead of 'endReached' since 'endReached'
        // is only triggered once (until log data changes); this ensures users
        // can try to fetch new logs repeatedly.
        atBottomStateChange={handleAtBottomStateChange}
        components={components}
        computeItemKey={computeItemKey}
        context={virtuosoContext}
        totalCount={globalLineCount}
        defaultItemHeight={lineHeight + 4}
        followOutput={followOutput}
        heightEstimates={virtuosoHeightEstimates}
        initialTopMostItemIndex={
          initialTopMostItemIndex ?? Math.max(globalLineCount - 1, 0)
        }
        isScrolling={handleIsScrolling}
        itemContent={itemContent}
        // Render additional logs to improve scroll performance for large lists.
        // Note that this value is arbitrary, but mostly eliminates flickering
        // when scrolling rapidly.
        increaseViewportBy={
          GLOBAL_LOG_EXTRA_RENDERED_ROW_COUNT * (lineHeight + 4)
        }
        rangeChanged={handleRangeChangedForViewer}
        ref={virtuosoRef as RefObject<VirtuosoHandle>}
        scrollSeekConfiguration={scrollSeekConfiguration}
        scrollerRef={setScrollerRef}
        // Prevent flickering when scrolling backwards; for context, see
        // https://github.com/petyosi/react-virtuoso/issues/1096#issuecomment-2262255320.
        skipAnimationFrameInResizeObserver={true}
        style={{height: '100%'}}
      />
      {showJumpToTop && (
        <LogJumpButton
          icon="chevron-up"
          label="Top"
          position="top"
          onPress={handleJumpToTop}
        />
      )}
      {showJumpToCurrent && (
        <LogJumpButton
          emphasis={!!newLineCount}
          icon="chevron-down"
          label={jumpToCurrentLabel}
          position="bottom"
          onPress={handleJumpToCurrent}
        />
      )}
      {isGlobalScrollStatusMounted && visibleRange != null && (
        <GlobalLogScrollStatus
          isLoading={showGlobalLoadingIndicator}
          isVisible={isGlobalScrollStatusVisible}
          range={visibleRange}
          totalCount={globalLineCount}
        />
      )}
    </>
  );
};

// empty placeholder line, we only want to render the empty space, avoid displaying line number
// since recalculation might break the users expectations
const GlobalLogPlaceholderLine: React.FC<{height: number}> = ({height}) => {
  return (
    <div
      role="row"
      aria-label="Loading log line"
      className="px-16 py-2"
      style={{height, boxSizing: 'border-box'}}
    />
  );
};

const GlobalLogScrollSeekPlaceholder: React.FC<{
  estimatedHeight?: number;
  height: number;
}> = ({estimatedHeight, height}) => {
  return (
    <GlobalLogPlaceholderLine height={Math.max(height, estimatedHeight ?? 0)} />
  );
};

const GlobalLogScrollStatus: React.FC<{
  isLoading: boolean;
  isVisible: boolean;
  range: ListRange;
  totalCount: number;
}> = ({isLoading, isVisible, range, totalCount}) => {
  const visibleMidpoint = Math.floor(
    (range.startIndex + range.endIndex + 2) / 2
  );
  const approximateLine = formatRoundedLogLineNumber(
    visibleMidpoint,
    totalCount
  );
  const approximateTotal = formatRoundedLogLineNumber(totalCount, totalCount);

  return (
    <div
      role="status"
      aria-label="Log scroll position"
      aria-busy={isLoading}
      className={twMerge(
        'pointer-events-none absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 justify-center transition-opacity duration-150 ease-out',
        isVisible ? 'opacity-100' : 'opacity-0'
      )}>
      <div className="relative rounded-md bg-white/95 px-12 py-6 font-sans text-xs font-medium tabular-nums text-moon-700 shadow-sm ring-1 ring-moon-200 backdrop-blur-sm dark:bg-wb-surface-raised dark:text-wb-text-primary dark:ring-wb-border">
        <span
          aria-hidden="true"
          className={twMerge(
            'absolute left-12 top-1/2 flex -translate-y-1/2 transition-opacity',
            isLoading ? 'opacity-100' : 'opacity-0'
          )}>
          <WaveLoader size="small" />
        </span>
        <span
          className={twMerge(
            'block transition-[padding]',
            isLoading ? 'pl-36' : 'pl-0'
          )}>
          ~{approximateLine} / {approximateTotal}
        </span>
      </div>
    </div>
  );
};

function formatRoundedLogLineNumber(value: number, totalCount: number) {
  if (totalCount <= 0) {
    return '0';
  }

  const rounded =
    Math.round(value / GLOBAL_LOG_SCROLL_STATUS_ROUNDING) *
    GLOBAL_LOG_SCROLL_STATUS_ROUNDING;
  return Math.min(totalCount, Math.max(1, rounded)).toLocaleString();
}

export function clampLogIndex(index: number, totalCount: number) {
  return Math.min(Math.max(0, index), Math.max(totalCount - 1, 0));
}

function useDelayedPresence(isPresent: boolean, hideDelayMs: number) {
  const [isMounted, setIsMounted] = useState(isPresent);
  const [isVisible, setIsVisible] = useState(isPresent);

  useEffect(() => {
    if (isPresent) {
      setIsMounted(true);
      const animationFrame = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });
      return () => {
        window.cancelAnimationFrame(animationFrame);
      };
    }

    setIsVisible(false);
    const timeout = window.setTimeout(() => {
      setIsMounted(false);
    }, hideDelayMs);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [hideDelayMs, isPresent]);

  return {isMounted: isPresent || isMounted, isVisible};
}

function buildGlobalLogHeightByNumber(
  lines: readonly (LogLineData & {number?: number})[],
  heightEstimates: number[] | null
) {
  if (heightEstimates == null) {
    return undefined;
  }

  const heightByNumber = new Map<number, number>();
  lines.forEach((line, index) => {
    if (isUsableGlobalLogNumber(line.number)) {
      heightByNumber.set(line.number, heightEstimates[index]);
    }
  });

  return heightByNumber.size > 0 ? heightByNumber : undefined;
}

function buildGlobalLogHeightEstimates({
  defaultHeight,
  heightByNumber,
  totalCount,
}: {
  defaultHeight: number;
  heightByNumber?: Map<number, number>;
  totalCount: number;
}) {
  if (
    heightByNumber == null ||
    totalCount <= 0 ||
    totalCount > GLOBAL_LOG_MAX_HEIGHT_ESTIMATE_COUNT
  ) {
    return undefined;
  }

  const estimates = Array<number>(totalCount).fill(defaultHeight);
  for (const [number, height] of heightByNumber) {
    if (number < totalCount) {
      estimates[number] = height;
    }
  }

  return estimates;
}

function getGlobalLogLineHeightStyle(estimatedHeight?: number) {
  return estimatedHeight == null
    ? undefined
    : {
        minHeight: estimatedHeight,
        boxSizing: 'border-box' as const,
      };
}

function isUsableGlobalLogNumber(number: unknown): number is number {
  return typeof number === 'number' && Number.isFinite(number) && number >= 0;
}

function isJumpToLogTopShortcut(event: KeyboardEvent) {
  return (
    (event.ctrlKey && event.key === 'Home') ||
    (event.metaKey && (event.key === 'Home' || event.key === 'ArrowUp'))
  );
}

function isJumpToLogCurrentShortcut(event: KeyboardEvent) {
  return (
    (event.ctrlKey && event.key === 'End') ||
    (event.metaKey && (event.key === 'End' || event.key === 'ArrowDown'))
  );
}

function isEditableKeyboardTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.closest('input, textarea, select, [contenteditable="true"]') != null
  );
}
